'use client'

import { useRef } from 'react'

import { ChevronLeft, ChevronRight } from 'lucide-react'

import { cn } from '@/lib/utils'
import { dateFromIso } from '@/i18n/calendar'
import { localeFormatter } from '@/i18n/format'

interface DateSelectorProps {
    dates: string[]
    selectedDate: string
    onDateChange: (date: string) => void
    /**
     * Render locale for the day/month names. Required (not defaulted to en-us) so a
     * caller can't silently ship an English date strip onto a localized page — the
     * names are universal CLDR data, so they come from `Intl`, not the translation
     * store (invariants.md#facts-canonical-store-editorial).
     */
    locale: string
}

export function shiftDate(dateStr: string, days: number): string {
    const date = new Date(dateStr + 'T12:00:00')
    date.setDate(date.getDate() + days)
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

/**
 * The strip's `{dayName, monthDay}` pair for a `YYYY-MM-DD` date, in the render locale.
 *
 * Built from explicit Y/M/D parts at UTC and read back through the UTC-pinned shared
 * formatter, so the rendered day equals the day the string names in every timezone
 * (invariants.md#ssr-safe-rendered-dates) — this is a client component, so its render
 * happens in the visitor's zone while the SSR pass happened in the server's.
 *
 * A date `dateFromIso` cannot read renders as an empty cell rather than a literal
 * "Invalid date" (→ `i18n-localization` FR-12a): a marker on the page is the defect, and
 * one bad entry costs its own cell's text rather than the strip. Routing through
 * `dateFromIso` first is also what keeps the shared formatter off a malformed value — it
 * logs an `IntlError` per format, which would be two console errors per day rendered, on
 * the server pass and again on hydration.
 */
function formatDate(dateStr: string, locale: string) {
    const date = dateFromIso(dateStr)
    if (!date) {
        return { dayName: '', monthDay: '' }
    }
    const format = localeFormatter(locale)
    return {
        dayName: format.dateTime(date, { weekday: 'short' }),
        monthDay: format.dateTime(date, { month: 'short', day: 'numeric' }),
    }
}

export function DateSelector({ dates, selectedDate, onDateChange, locale }: DateSelectorProps) {
    const containerRef = useRef<HTMLOListElement>(null)

    function scrollLeft() {
        const el = containerRef.current
        if (!el?.scrollBy) {
            return
        }
        el.scrollBy({ left: -el.clientWidth, behavior: 'smooth' })
    }

    function scrollRight() {
        const el = containerRef.current
        if (!el?.scrollBy) {
            return
        }
        el.scrollBy({ left: el.clientWidth, behavior: 'smooth' })
    }

    return (
        <div className="flex items-center py-4 md:py-12">
            <button
                type="button"
                aria-label="Previous dates"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[#2e3033] hover:bg-[#eaebec] disabled:text-[#b6bdc7]"
                onClick={scrollLeft}
            >
                <ChevronLeft className="h-5 w-5" />
            </button>

            <ol
                ref={containerRef}
                className="scrollbar-none flex min-w-0 flex-1 gap-1 overflow-x-auto"
            >
                {dates.map((date) => {
                    const { dayName, monthDay } = formatDate(date, locale)
                    const isSelected = date === selectedDate

                    return (
                        <li key={date}>
                            <button
                                type="button"
                                className={cn(
                                    'flex h-[64px] w-[80px] flex-col items-center justify-center rounded-[7px] text-sm transition-colors md:h-[72px] md:w-[88px] md:text-base',
                                    isSelected
                                        ? 'bg-[#188da1] text-white'
                                        : 'text-[#2e3033] hover:bg-[#eaebec]',
                                )}
                                onClick={() => onDateChange(date)}
                            >
                                <span className="font-bold">{dayName}</span>
                                <span className="font-bold">{monthDay}</span>
                            </button>
                        </li>
                    )
                })}
            </ol>

            <button
                type="button"
                aria-label="Next dates"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[#2e3033] hover:bg-[#eaebec] disabled:text-[#b6bdc7]"
                onClick={scrollRight}
            >
                <ChevronRight className="h-5 w-5" />
            </button>
        </div>
    )
}
